/**
 * CannedChipsRow — S2 Task 8 (spec 2026-07-16 §3.1/§4, veredito visual TL).
 *
 * Chips REAIS das Respostas Prontas vinculadas ao playbook ativo da
 * conversa (Composer Linha 4) — mata as pills fake hardcoded que existiam
 * em `QuickRepliesPills` (4 fixtures fixas, decisão superada por esta
 * task). Clique NUNCA envia — `onSelect` alimenta a fila de revisão do
 * atendente (mesmo contrato do `CannedResponsePicker`, consumida pela
 * Task 9 via `stagedResponse` no Composer). O chip dashed "Biblioteca…"
 * abre o picker completo via `onOpenLibrary` (modo navegação).
 *
 * Reusa as classes DS existentes `.composer-r7b__quickreplies` /
 * `.composer-r7b__chip` / `.composer-r7b__chip--more` (Caso A ADR-141 — o
 * DS já cobre este visual, herdado das pills que morreram).
 *
 * Gate-the-fetch (`feedback_gatear_fetch_nao_so_render`): o fetch só
 * dispara com a cap presente E playbook ativo — sem duplicar rede com o
 * `CannedResponsePicker` (mesma queryKey sem params, React Query reusa o
 * cache quando ambos estão montados).
 *
 * Retorna `null` (sem placeholder) em QUALQUER destas 3 condições — sem
 * cap `canned-response-use`, 403 do Engine (server-authoritative), ou zero
 * respostas vinculadas ao playbook ativo. A linha some inteira; nunca
 * mostra um estado vazio.
 */
import { useCannedResponses } from '../../../../hooks/use-canned-responses';
import { useCapability } from '../../../../hooks/use-capability';
import { ApiError, type CannedResponseWire } from '../../../../lib/api-client';

import type { JSX } from 'react';

const MAX_CHIPS = 4;

export function CannedChipsRow({
  playbookId,
  onSelect,
  onOpenLibrary,
}: {
  playbookId: string | null;
  onSelect: (response: CannedResponseWire) => void;
  onOpenLibrary: () => void;
}): JSX.Element | null {
  const hasCap = useCapability('canned-response-use');
  const listQ = useCannedResponses(hasCap && playbookId !== null);

  if (!hasCap || playbookId === null) return null;
  if (listQ.isError && listQ.error instanceof ApiError && listQ.error.status === 403) {
    return null;
  }

  const linked = (listQ.data?.data ?? []).filter((r) => r.playbookIds.includes(playbookId));
  if (linked.length === 0) return null;

  const visible = linked.slice(0, MAX_CHIPS);

  return (
    <div className="flex flex-col gap-1">
      <div className="composer-r7b__quickreplies" data-testid="canned-chips-row">
        {visible.map((response) => (
          <button
            key={response.id}
            type="button"
            className="composer-r7b__chip"
            onClick={() => onSelect(response)}
            title={`Revisar antes de enviar: ${response.title}`}
          >
            {response.title} · {response.items.length.toString()}{' '}
            {response.items.length === 1 ? 'item' : 'itens'}
          </button>
        ))}
        <button
          type="button"
          className="composer-r7b__chip composer-r7b__chip--more"
          onClick={onOpenLibrary}
          title="Abrir biblioteca completa de respostas prontas"
        >
          Biblioteca…
        </button>
      </div>
      <p className="text-2xs" style={{ color: 'var(--fg-4)' }}>
        Selecionar abre a fila de revisão antes de enviar — nunca envia direto.
      </p>
    </div>
  );
}
